import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { AdminError, ERRS, type Row } from "./api";
import {
  FieldError, Help, linkLabel, noIntercomLink, Pick, productChoices, refusalLink, SAVE_FAILED, stepDose, stepOptions,
  subGoals, topGoals,
} from "./ChangeProgramme";
import { useAdmin } from "./context";
import { caseFor, day, L, ml, weekA, when } from "./format";

/** DEC-1354 PA-START-COPY-1c lines 1–15. Keep sign-off edits confined to this object. */
export const CREATE_COPY = {
  button: "Create programme",
  customerLine: "She answered the start questions on {date}. Create a programme to give her a first dose.",
  noneYet: "None yet",
  noCheckins: "No check-ins yet.",
  title: "Create programme: {first}",
  now: "Now: no programme. Her start answers from {date} set her target: {target} ml (step {n}).",
  goalHelp: "From her start answers. Pick another if she told you a different goal.",
  productHelp: "Pick the product she has.",
  startStep: "Start step",
  caseTypeHelp: "New case type: Create programme.",
  before: "None",
  confirm: "Create programme",
  created: "created: step {n} · {dose} ml · journey v1",
  noStartAnswers: "She has no start answers to build a plan from.",
  noStartNext: "Ask her to answer the start questions in the app. Then create her programme here.",
} as const;

export const CREATE_COPY_DRAFT = false;  // DEC-1354 A (Damien, 4 Nov): lines 1-24 signed as drawn

export type CreateForm = {
  goal_top: string;
  goal_level_2: string;
  product_key: string;
  step: string;
  weekly_step_ml: string;
  instructions: string;
  reason: string;
  case_type: string;
  link: string;
};

export function startAnswer(d: Row): Row | undefined {
  const direct = d.start_answers || d.start;
  if (direct && typeof direct === "object") return direct as Row;
  return ((d.checkins || []) as Row[]).find((x) => x.kind === "start");
}

export function startAnswerDate(d: Row): unknown {
  const s = startAnswer(d);
  return s?.answered_at ?? s?.at ?? d.start_answered_at;
}

export function customerCreateLine(d: Row): string {
  const at = startAnswerDate(d);
  return at
    ? CREATE_COPY.customerLine.replace("{date}", day(at as string))
    : CREATE_COPY.customerLine.replace(" on {date}", "");
}

export function createSummary(pv: Row | undefined, d: Row): string {
  if (!pv) return "Now: no programme.";
  const steps = (pv.steps || []) as Row[];
  const targetStep = steps.find((x) => x.target) || steps[steps.length - 1];
  const at = startAnswerDate(d);
  return CREATE_COPY.now
    .replace(" from {date}", at ? ` from ${day(at as string)}` : "")
    .replace("{target}", String(pv.target_dose_ml ?? "—"))
    .replace("{n}", String(targetStep?.step ?? "—"));
}

export function createBody(f: CreateForm, pv: Row, d: Row): Row {
  const body: Row = {
    reason: f.reason.trim(),
    case_type: f.case_type,
    goal_level_2: f.goal_level_2,
    product_key: f.product_key,
  };
  const cid = caseFor(f.link, d.overrides as Row[] | undefined);
  if (cid) body.case_id = cid;
  if (f.link) {
    const [kind, id] = f.link.split(":");
    body[kind === "alert" ? "team_alert_id" : "handoff_id"] = id;
  }
  const dose = stepDose((pv.steps || []) as Row[], f.step);
  if (f.step) body.step = Number(f.step);
  if (dose != null) body.dose_ml = dose;
  const w = f.weekly_step_ml.trim() === "" ? null : Number(f.weekly_step_ml);
  if (w != null && Number.isFinite(w) && w !== Number(pv.weekly_step_default_ml)) body.weekly_step_ml = w;
  if (f.instructions.trim()) body.instructions = f.instructions.trim();
  return body;
}

/**
 * The preview request. Every nonblank weekly increase is sent: the default differs by goal and product, so comparing
 * with the last preview's default could drop a value that is not the new product's default (os-portal PR 863 review).
 */
export function createPreviewBody(f: Pick<CreateForm, "goal_level_2" | "product_key" | "weekly_step_ml">): Row {
  const body: Row = { goal_level_2: f.goal_level_2, product_key: f.product_key };
  const w = f.weekly_step_ml.trim() === "" ? null : Number(f.weekly_step_ml);
  if (w != null && Number.isFinite(w)) body.weekly_step_ml = w;
  return body;
}

const Draft = () => CREATE_COPY_DRAFT ? <span className="text-xs font-semibold text-destructive" data-draft>DRAFT</span> : null;

export function CreateProgramme({ id, d, openA, openH, onSaved }: {
  id: string; d: Row; openA: Row[]; openH: Row[]; onSaved: (message: string) => void;
}) {
  const { key, api, caseTypes, product, options } = useAdmin();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<"edit" | "review">("edit");
  const [formError, setFormError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pr: Row = d.profile || {};
  const first = String(pr.name || "").trim().split(/\s+/)[0] || "She";
  const goals = ((options?.goals || []) as Row[]);
  const products = ((options?.products || []) as Row[]);
  const start = startAnswer(d);
  const initialGoal = String(start?.goal_level_2 ?? d.goal_level_2 ?? "");
  const initialTop = String(start?.goal ?? goals.find((g) => g.goal_level_2 === initialGoal)?.goal ?? "");
  const links = useMemo<[string, string][]>(() => [
    ...openA.map((a): [string, string] => [`alert:${a.id}`, `Alert: ${L(a.type)}, ${when(a.at)}`]),
    ...openH.map((h): [string, string] => [`handoff:${h.id}`, `Hand-off: ${h.goal || ""}, ${when(h.at)}`]),
  ], [openA, openH]);
  const createType = caseTypes.some(([k]) => k === "create_programme") ? "create_programme" : "";
  const blank = (): CreateForm => ({
    goal_top: initialTop, goal_level_2: initialGoal, product_key: "", step: "", weekly_step_ml: "", instructions: "",
    reason: "", case_type: createType, link: links[0]?.[0] ?? "",
  });
  const [f, setF] = useState<CreateForm>(blank);
  const set = (patch: Partial<CreateForm>) => setF((old) => ({ ...old, ...patch }));
  const enc = encodeURIComponent(id);

  useEffect(() => {
    if (open) {
      setStage("edit"); setFormError(null); setSaveError(null); setRefusal(null); setF(blank());
    }
    // Reset from the current detail/options only when a new sheet session begins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const wnum = f.weekly_step_ml.trim() === "" ? null : Number(f.weekly_step_ml);
  const previewBody = createPreviewBody(f);
  const preview = useQuery({
    queryKey: [key, "customer", id, "create-preview", f.goal_level_2, f.product_key, f.weekly_step_ml],
    queryFn: () => api.post<Row>(`customers/${enc}/create-preview`, previewBody),
    enabled: open && !!f.goal_level_2 && !!f.product_key && (wnum == null || (Number.isFinite(wnum) && wnum >= 0)),
    retry: false,
  });
  const pv = preview.data;
  const steps = ((pv?.steps || []) as Row[]);
  useEffect(() => {
    if (pv?.start_step != null) set({ step: String(pv.start_step) });
    // A changed preview proposes its own start step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.dataUpdatedAt]);

  const tops = topGoals(goals);
  const subs = subGoals(goals, f.goal_top, initialGoal && f.goal_top === initialTop ? initialGoal : "");
  const productOptions = productChoices(products, "", undefined);
  const pickedDose = stepDose(steps, f.step);
  const ticket = [...openH, ...openA].map((x) => x.ticket).find(Boolean) as string | undefined;
  const ic = useQuery({
    queryKey: [key, "customer", id, "intercom"],
    queryFn: () => api.get<Row>(`customers/${enc}/intercom`).catch(() => ({ items: [], note: "intercom_error" })),
    enabled: open && refusal === "no_start_answers" && !ticket,
  });
  const icHref = refusalLink(ticket, ic.data);

  const check = (): string | null => {
    if (!f.reason.trim()) return "reason_required";
    if (!f.case_type) return "unknown_case_type";
    if (!f.goal_level_2) return "goal_required";
    if (!f.product_key) return "product_required";
    if (wnum != null && (!Number.isFinite(wnum) || wnum < 0)) return "bad_weekly_step";
    if (!f.step || pickedDose == null) return "no_dose";
    return null;
  };

  const save = async () => {
    if (!pv) return;
    setBusy(true); setSaveError(null); setRefusal(null);
    try {
      await api.post<Row>(`customers/${enc}/create-program`, createBody(f, pv, d));
      setOpen(false);
      onSaved(CREATE_COPY.created.replace("{n}", f.step).replace("{dose}", String(pickedDose)));
    } catch (error) {
      const k = error instanceof AdminError ? error.key : null;
      setStage("edit");
      if (k === "no_start_answers" || k === "has_active_run" || k === "busy") setRefusal(k);
      else {
        const message = error instanceof Error ? error.message : String(error);
        setSaveError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const closeAndRefresh = () => { setOpen(false); onSaved(""); };
  const reviewRows: [string, string][] = pv ? [
    ["Goal", f.goal_level_2],
    ["Product", product(f.product_key)],
    [CREATE_COPY.startStep, `${f.step} · ${pickedDose} ml`],
    ["Weekly increase", weekA(wnum ?? pv.weekly_step_default_ml)],
    ["Target", ml(pv.target_dose_ml)],
    ...(f.instructions.trim() ? [["Instructions", f.instructions.trim()] as [string, string]] : []),
    ["Reason", f.reason.trim()],
    ["Case type", caseTypes.find(([k]) => k === f.case_type)?.[1] ?? f.case_type],
    ["Answers", linkLabel(links, f.link)],
  ] : [];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild><Button>{CREATE_COPY.button}</Button></SheetTrigger>
      <SheetContent className="w-[480px] overflow-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">{CREATE_COPY.title.replace("{first}", first)} <Draft /></SheetTitle>
          <SheetDescription>{createSummary(pv, d)}</SheetDescription>
        </SheetHeader>
        {stage === "edit" ? (
          <div className="space-y-3 px-4 pb-4 text-sm">
            {refusal === "no_start_answers" ? (
              <Alert role="alert"><AlertDescription className="space-y-2">
                <p>{CREATE_COPY.noStartAnswers}</p><p>{CREATE_COPY.noStartNext}</p>
                {icHref ? <Button size="sm" variant="secondary" asChild><a href={icHref} target="_blank" rel="noopener noreferrer" data-nonmutating>Open in Intercom</a></Button>
                  : ic.isLoading ? <p className="text-ks-grey-1">Finding her Intercom conversation…</p>
                    : <p>{noIntercomLink(ic.data?.note, pr.email)}</p>}
              </AlertDescription></Alert>
            ) : refusal === "has_active_run" || refusal === "busy" ? (
              <Alert role="alert"><AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>{ERRS[refusal]}</span><Button size="sm" variant="secondary" data-nonmutating onClick={closeAndRefresh}>Close</Button>
              </AlertDescription></Alert>
            ) : null}
            {saveError ? <Alert role="alert"><AlertDescription><p>{saveError}</p><p>{SAVE_FAILED}</p></AlertDescription></Alert> : null}
            <div><Label htmlFor="pa-create-goal">Goal</Label>
              <Pick id="pa-create-goal" value={f.goal_top} onChange={(goal_top) => set({ goal_top, goal_level_2: "", step: "" })}
                options={[["", "Pick one"], ...tops.map((g): [string, string] => [g, g])]} />
              {f.goal_top ? <div className="mt-2"><Label htmlFor="pa-create-sub">Which one</Label>
                <Pick id="pa-create-sub" value={f.goal_level_2} onChange={(goal_level_2) => set({ goal_level_2, step: "" })}
                  options={[["", "Pick one"], ...subs.map((g): [string, string] => [g.goal_level_2, g.goal_level_2])]} /></div> : null}
              <Help>{CREATE_COPY.goalHelp}</Help></div>
            <div><Label htmlFor="pa-create-product">Product</Label>
              <Pick id="pa-create-product" value={f.product_key} onChange={(product_key) => set({ product_key, step: "" })}
                options={[["", "Pick one"], ...productOptions]} />
              <Help>{CREATE_COPY.productHelp}</Help></div>
            <div><Label htmlFor="pa-create-step">{CREATE_COPY.startStep}</Label>
              <Pick id="pa-create-step" value={f.step} onChange={(step) => set({ step })}
                disabled={!f.goal_level_2 || !f.product_key || preview.isLoading || !pv}
                invalid={!!preview.error || formError === ERRS.no_dose}
                options={steps.length ? stepOptions(steps, null) : [["", preview.isLoading ? "…" : "Pick one"]]} />
              {preview.error ? <FieldError id="pa-create-step-error">{(preview.error as Error).message}</FieldError>
                : formError === ERRS.no_dose ? <FieldError id="pa-create-step-error">{formError}</FieldError> : null}</div>
            <div><Label htmlFor="pa-create-weekly">Weekly increase (ml a week)</Label>
              <Input id="pa-create-weekly" type="number" step="any" min="0" value={f.weekly_step_ml}
                onChange={(e) => set({ weekly_step_ml: e.target.value, step: "" })} />
              <Help>0 holds the dose.{pv?.weekly_step_default_ml != null ? ` Product default: ${pv.weekly_step_default_ml} ml a week.` : ""}</Help></div>
            <div><Label htmlFor="pa-create-instructions">How to take it (she sees this)</Label>
              <Textarea id="pa-create-instructions" rows={2} maxLength={500} value={f.instructions} onChange={(e) => set({ instructions: e.target.value })} /></div>
            <div><Label htmlFor="pa-create-reason">Reason (required)</Label>
              <Textarea id="pa-create-reason" rows={3} value={f.reason} onChange={(e) => set({ reason: e.target.value })} />
              <Help>Write why. It’s how we improve the algorithm.</Help></div>
            <div><Label htmlFor="pa-create-case-type">Case type</Label>
              <Pick id="pa-create-case-type" value={f.case_type} onChange={(case_type) => set({ case_type })} options={[["", "Pick one"], ...caseTypes]} />
              <Help>{CREATE_COPY.caseTypeHelp}</Help></div>
            {links.length ? <div><Label htmlFor="pa-create-answers">Answers</Label>
              <Pick id="pa-create-answers" value={f.link} onChange={(link) => set({ link })} options={[["", "Nothing open"], ...links]} /></div> : null}
            {formError && formError !== ERRS.no_dose ? <Alert role="alert"><AlertDescription>{formError}</AlertDescription></Alert> : null}
            <div className="flex gap-2">
              <Button variant="outline" data-nonmutating onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-nonmutating disabled={preview.isLoading || !!preview.error} onClick={() => {
                const k = check();
                if (k) { setFormError(ERRS[k]); return; }
                setFormError(null); setSaveError(null); setRefusal(null); setStage("review");
              }}>See before and after</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 px-4 pb-4 text-sm">
            <Table><TableHeader><TableRow><TableHead /><TableHead>Before</TableHead><TableHead>After</TableHead></TableRow></TableHeader>
              <TableBody>{reviewRows.map(([label, after]) => <TableRow key={label}>
                <TableHead>{label}</TableHead><TableCell>{CREATE_COPY.before}</TableCell><TableCell className="whitespace-normal font-semibold">{after}</TableCell>
              </TableRow>)}</TableBody></Table>
            <div className="flex gap-2">
              <Button variant="outline" data-nonmutating onClick={() => setStage("edit")}>Back</Button>
              <Button disabled={busy} onClick={() => void save()}>{CREATE_COPY.confirm}</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
